import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Bell,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Download,
  Eye,
  Facebook,
  GraduationCap,
  Hourglass,
  Instagram,
  LayoutDashboard,
  Linkedin,
  Menu as MenuIcon,
  MessageSquare,
  Package,
  PlayCircle,
  Search,
  ShieldCheck,
  ShieldHalf,
  Sparkles,
  TrendingUp,
  Twitter,
  Users,
  Zap,
} from "lucide-react";
import { AnimatePresence, motion, useScroll, useSpring, useTransform } from "framer-motion";
import ContactFab, { ContactModal } from "@/components/ContactFab";
import BrandMark from "@/components/layout/BrandMark";
import { EventChip, type ChipTone } from "@/components/shared/EventChip";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useLandingContent } from "@/hooks/useLandingContent";
import type { LandingContent } from "@/data/landingContent";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

function TypewriterText({ text, onComplete }: { text: string; onComplete: () => void }) {
  const [displayed, setDisplayed] = useState("");
  const [idx, setIdx] = useState(0);
  const completeRef = useRef(onComplete);
  const doneRef = useRef(false);
  completeRef.current = onComplete;

  useEffect(() => {
    setDisplayed("");
    setIdx(0);
    doneRef.current = false;
  }, [text]);

  useEffect(() => {
    if (idx < text.length) {
      const timer = setTimeout(() => {
        setDisplayed((prev) => prev + text[idx]);
        setIdx(idx + 1);
      }, 20 + Math.random() * 18);
      return () => clearTimeout(timer);
    }
    if (idx === text.length && !doneRef.current) {
      doneRef.current = true;
      const timer = setTimeout(() => completeRef.current(), 2600);
      return () => clearTimeout(timer);
    }
  }, [idx, text]);

  const isTyping = idx < text.length;
  return (
    <>
      {displayed}
      <span
        className={cn(
          "ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] rounded-full bg-foreground",
          isTyping ? "opacity-100" : "animate-pulse",
        )}
        aria-hidden
      />
    </>
  );
}

const navLinks = [
  { label: "Platform", href: "#platform" },
  { label: "Services", href: "#services" },
  { label: "Audiences", href: "#audiences" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Contact", to: "/contact" as const },
];

// Icon shapes are presentation, not content, so they stay in code and are
// matched to CMS list items by position (and, for socials, by platform name).
const serviceIcons = [<Zap />, <BarChart3 />, <ClipboardCheck />, <Briefcase />, <Package />, <ShieldHalf />];
const serviceTones: ChipTone[] = ["lavender", "peach", "mint", "sky", "lilac", "rose"];
const audienceIcons = [<GraduationCap />, <Briefcase />, <ShieldCheck />];
const audienceTones: ChipTone[] = ["lavender", "peach", "mint"];
const logIcons = [<Users />, <ClipboardList />, <CheckCircle2 />, <Download />];
const logTones: ChipTone[] = ["lavender", "peach", "mint", "lilac"];
const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
} as const;

const TONE_BG: Record<ChipTone, string> = {
  lavender: "bg-pastel-lavender text-pastel-lavender-ink",
  peach: "bg-pastel-peach text-pastel-peach-ink",
  rose: "bg-pastel-rose text-pastel-rose-ink",
  sky: "bg-pastel-sky text-pastel-sky-ink",
  mint: "bg-pastel-mint text-pastel-mint-ink",
  lilac: "bg-pastel-lilac text-pastel-lilac-ink",
  muted: "bg-muted text-body-strong",
};

/** Renders a headline with one phrase set in Instrument Serif italic. */
function EmphasisTitle({ text, emphasis }: { text: string; emphasis?: string }) {
  if (!emphasis || !text.includes(emphasis)) return <>{text}</>;
  const [before, after] = text.split(emphasis);
  return (
    <>
      {before}
      <span className="font-serif text-[1.1em] leading-none font-normal tracking-[-0.01em] italic">
        {emphasis}
      </span>
      {after}
    </>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
};

function LandingPage() {
  const content = useLandingContent();
  const slides = content.hero.slides;
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [activeHero, setActiveHero] = useState(0);
  const [typingKey, setTypingKey] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToNextSlide = useCallback(() => {
    setActiveHero((prev) => (prev + 1) % slides.length);
    setTypingKey((k) => k + 1);
  }, [slides.length]);

  const goToSlide = useCallback((i: number) => {
    setActiveHero(i);
    setTypingKey((k) => k + 1);
  }, []);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.2 });

  // The product preview tilts back into place as the hero scrolls away.
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const previewTilt = useTransform(heroProgress, [0, 0.45], [14, 0]);
  const previewScale = useTransform(heroProgress, [0, 0.45], [0.94, 1]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
  }, []);

  const handleScroll = (event: React.MouseEvent<HTMLElement>, href: string) => {
    event.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 96;
      const targetY = element.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(targetY, 0), behavior: "smooth" });
      setMenuOpen(false);
    }
  };

  const slide = slides[activeHero] ?? slides[0];

  const navLinkClass =
    "rounded-full px-3.5 py-2 text-sm text-body transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="min-h-dvh overflow-x-clip bg-background text-foreground">
      <motion.div
        aria-hidden
        className="fixed inset-x-0 top-0 z-[60] h-0.5 origin-left bg-foreground"
        style={{ scaleX: progress }}
      />

      {/* ─── TOP NAV ─────────────────────────────── */}
      <header className="sticky top-0 z-50 px-3 pt-3 md:px-6 md:pt-4">
        <div
          className={cn(
            "mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 rounded-full border pr-2 pl-5 transition-all duration-300 md:h-16 md:pr-2.5 md:pl-6",
            scrolled
              ? "border-border bg-card/85 shadow-panel backdrop-blur-xl"
              : "border-transparent bg-transparent",
          )}
        >
          <Link
            to="/"
            aria-label="Taskpart home"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <BrandMark />
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-0.5 lg:flex">
            {navLinks.map((link) =>
              link.to ? (
                <Link key={link.label} to={link.to} className={navLinkClass}>
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleScroll(e, link.href!)}
                  className={navLinkClass}
                >
                  {link.label}
                </a>
              ),
            )}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <Button variant="ghost" size="sm" asChild className="hidden h-10 px-4 sm:inline-flex">
              <Link to="/auth/login">Sign in</Link>
            </Button>
            <Button size="sm" className="h-10 px-4" onClick={() => setContactOpen(true)}>
              Get in touch
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="lg:hidden"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <MenuIcon />
            </Button>
          </div>
        </div>
      </header>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="flex w-[84vw] flex-col gap-0 p-0 sm:max-w-[340px]">
          <div className="flex h-16 items-center px-5">
            <BrandMark />
          </div>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Site navigation</SheetDescription>
          <nav aria-label="Mobile" className="flex flex-col gap-1 p-3">
            {navLinks.map((link) =>
              link.to ? (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-full px-4 py-3 text-[15px] text-body-strong hover:bg-muted"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleScroll(e, link.href!)}
                  className="rounded-full px-4 py-3 text-[15px] text-body-strong hover:bg-muted"
                >
                  {link.label}
                </a>
              ),
            )}
          </nav>
          <div className="mt-auto flex flex-col gap-2 p-5">
            <Button variant="outline" asChild>
              <Link to="/auth/login">Sign in</Link>
            </Button>
            <Button asChild>
              <Link to="/auth/register">Create account</Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── HERO ────────────────────────────────── */}
      <section id="top" ref={heroRef} className="relative -mt-[72px] pt-[72px] md:-mt-20 md:pt-20">
        <HeroBackdrop />

        <div className="relative mx-auto max-w-6xl px-4 pt-14 text-left md:px-6 md:pt-20">
          <AnimatePresence mode="wait">
            <motion.div
              key={`hero-${activeHero}`}
              initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 py-1 pr-3.5 pl-1 text-[13px] text-body shadow-soft backdrop-blur">
                <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-medium text-primary-foreground">
                  New
                </span>
                {slide.eyebrow}
              </span>
              <h1 className="mt-7 max-w-4xl text-[38px] leading-[44px] font-medium tracking-[-1.4px] text-balance text-foreground sm:text-[52px] sm:leading-[58px] sm:tracking-[-2px] lg:text-[68px] lg:leading-[72px] lg:tracking-[-2.8px]">
                <EmphasisTitle text={slide.title} emphasis={slide.emphasis} />
              </h1>
              <p className="mt-6 max-w-2xl text-body-md text-pretty text-body md:text-body-lg">
                {slide.description}
              </p>
              <div className="mt-9 flex flex-col justify-start gap-2.5 sm:flex-row">
                <Button size="lg" asChild>
                  <a href={slide.primaryTo}>
                    {slide.primaryLabel}
                    <ArrowRight />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a
                    href={`#${slide.secondaryAction}`}
                    onClick={(e) => handleScroll(e, `#${slide.secondaryAction}`)}
                  >
                    <PlayCircle />
                    {slide.secondaryLabel}
                  </a>
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Social proof — NEVER TOUCH */}
          <div className="mt-10 flex items-center justify-start gap-3">
            <div className="flex -space-x-2.5">
              {content.hero.avatars.map((src, i) => (
                <Avatar key={i} className="size-9 border-2 border-background md:size-10">
                  <AvatarImage src={src} alt="" />
                  <AvatarFallback className="text-caption">TP</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <div className="text-left">
              <p className="text-body-md font-medium text-foreground tabular-nums">
                {content.hero.socialProofCount}
              </p>
              <p className="text-caption text-muted-foreground">{content.hero.socialProofLabel}</p>
            </div>
          </div>

          {/* Slide controls */}
          <div className="mt-8 flex items-center justify-start gap-1.5" role="tablist" aria-label="Hero slides">
            {slides.map((s, i) => (
              <button
                key={s.eyebrow}
                type="button"
                role="tab"
                aria-selected={i === activeHero}
                aria-label={`Show slide ${i + 1}: ${s.eyebrow}`}
                onClick={() => goToSlide(i)}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  i === activeHero ? "w-8 bg-foreground" : "w-3 bg-hairline-strong hover:bg-mute",
                )}
              />
            ))}
          </div>
        </div>

        {/* Product preview */}
        <div className="relative mx-auto mt-14 max-w-6xl px-3 pb-6 md:mt-16 md:px-6 [perspective:1600px]">
          <motion.div
            style={{ rotateX: previewTilt, scale: previewScale }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative origin-top"
          >
            <ProductPreview />

            {/* Floating insight card: the typewriter drives the slide rotation */}
            <div className="absolute -bottom-6 left-3 z-10 w-[min(360px,calc(100%-1.5rem))] md:-bottom-10 md:-left-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`insight-${activeHero}`}
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="rounded-2xl border border-border bg-card/95 p-4 text-left shadow-elevated backdrop-blur md:p-5"
                >
                  <div className="flex items-center gap-2">
                    <span className="flex size-8 items-center justify-center rounded-full bg-pastel-lilac text-pastel-lilac-ink">
                      <Sparkles className="size-4" />
                    </span>
                    <p className="text-sm font-medium text-foreground">{slide.insightTitle}</p>
                    <span className="ml-auto flex items-center gap-1.5 rounded-full bg-pastel-mint px-2 py-0.5 text-[11px] font-medium text-pastel-mint-ink">
                      <span className="size-1.5 animate-pulse rounded-full bg-current" />
                      Live
                    </span>
                  </div>
                  <p className="mt-3 min-h-[5.5em] text-[13px] leading-[1.6] text-body-strong" aria-live="off">
                    <TypewriterText
                      key={`tw-${typingKey}-${activeHero}`}
                      text={slide.insightBody}
                      onComplete={goToNextSlide}
                    />
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Floating activity log from the slide */}
            <div className="absolute -top-12 right-3 z-10 hidden w-[300px] md:block lg:-right-10">
              <AnimatePresence mode="wait">
                <motion.ul
                  key={`log-${activeHero}`}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="space-y-2 rounded-2xl border border-border bg-card/95 p-3 shadow-elevated backdrop-blur"
                >
                  {slide.log.map((line, i) => (
                    <li key={line.t + line.k} className="flex items-center gap-2">
                      <span className="w-10 shrink-0 text-[11px] text-muted-foreground tabular-nums">{line.t}</span>
                      <EventChip
                        tone={logTones[i % logTones.length]}
                        icon={logIcons[i % logIcons.length]}
                        title={line.k.charAt(0).toUpperCase() + line.k.slice(1)}
                        meta={line.v}
                        className="min-w-0 flex-1"
                      />
                    </li>
                  ))}
                </motion.ul>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── PARTNER LOGOS ───────────────────────── */}
      <section aria-labelledby="partners-heading" className="pt-20 pb-6 md:pt-28">
        <p id="partners-heading" className="mb-8 px-4 text-center text-sm text-muted-foreground">
          {content.partnerHeading}
        </p>
        <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
          <ul className="flex w-max animate-[marquee_32s_linear_infinite] gap-4 hover:[animation-play-state:paused] motion-reduce:animate-none">
            {[...content.partnerLogos, ...content.partnerLogos, ...content.partnerLogos].map((partner, i) => (
              <li
                key={`${partner}-${i}`}
                aria-hidden={i >= content.partnerLogos.length || undefined}
                className="flex h-16 shrink-0 items-center gap-2.5 rounded-full border border-border bg-card px-7 text-[15px] font-medium tracking-[-0.2px] text-body shadow-soft"
              >
                <span className={cn("size-2.5 rounded-full", ["bg-pastel-lavender-ink", "bg-pastel-peach-ink", "bg-pastel-mint-ink", "bg-pastel-sky-ink", "bg-pastel-rose-ink"][i % 5])} />
                {partner}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── APPROACH & STATS ────────────────────── */}
      <Section id="platform">
        <div className="grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <SectionKicker>{content.approach.kicker}</SectionKicker>
            <SectionTitle>
              {content.approach.titleLine1}
              <br />
              <span className="font-serif font-normal italic">{content.approach.titleEmphasis}</span>
            </SectionTitle>
            <SectionLead>{content.approach.lead}</SectionLead>
            <Button variant="outline" asChild className="mt-8">
              <Link to="/auth/login">
                {content.approach.ctaLabel}
                <ArrowRight />
              </Link>
            </Button>
          </motion.div>
          <div className="grid grid-cols-1 gap-3 min-[460px]:grid-cols-2 md:gap-4">
            {content.stats.map((s, i) => (
              <StatTile key={s.label} stat={s} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* ─── SERVICES ────────────────────────────── */}
      <Section id="services" tinted>
        <SplitHeading
          kicker={content.services.kicker}
          title={content.services.title}
          lead={content.services.lead}
        />
        <div className="grid gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
          {content.services.items.map((s, i) => {
            const tone = serviceTones[i % serviceTones.length];
            return (
              <motion.article
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-panel md:p-7"
              >
                <div
                  aria-hidden
                  className={cn(
                    "pointer-events-none absolute -top-16 -right-16 size-40 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-80",
                    TONE_BG[tone].split(" ")[0],
                  )}
                />
                <div className={cn("relative mb-8 flex size-12 items-center justify-center rounded-2xl [&_svg]:size-5", TONE_BG[tone])}>
                  {serviceIcons[i % serviceIcons.length]}
                </div>
                <h3 className="relative text-title text-foreground">{s.title}</h3>
                <p className="relative mt-2 text-sm leading-6 text-body">{s.text}</p>
              </motion.article>
            );
          })}
        </div>
      </Section>

      {/* ─── HOW IT WORKS ────────────────────────── */}
      <Section id="how-it-works">
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <SectionKicker>{content.howItWorks.kicker}</SectionKicker>
          <SectionTitle>
            <EmphasisTitle text={content.howItWorks.title} emphasis={content.howItWorks.titleEmphasis} />
          </SectionTitle>
          <SectionLead className="mx-auto">{content.howItWorks.lead}</SectionLead>
        </div>
        <HowItWorksTimeline steps={content.howItWorks.steps} />
      </Section>

      {/* ─── AUDIENCES ───────────────────────────── */}
      <Section id="audiences" tinted>
        <SplitHeading
          kicker={content.audiences.kicker}
          title={content.audiences.title}
          lead={content.audiences.lead}
        />
        <div className="grid gap-3 md:grid-cols-3 md:gap-4">
          {content.audiences.items.map((a, i) => {
            const tone = audienceTones[i % audienceTones.length];
            return (
              <motion.article
                key={a.title}
                {...fadeUp}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="flex flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-soft"
              >
                <div className={cn("relative h-28", TONE_BG[tone].split(" ")[0])}>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_-20%,rgb(255_255_255/0.7),transparent_60%)] dark:opacity-20" />
                  <div className="absolute bottom-0 left-6 flex size-14 translate-y-1/2 items-center justify-center rounded-2xl border border-border bg-card text-foreground shadow-soft [&_svg]:size-6">
                    {audienceIcons[i % audienceIcons.length]}
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6 pt-11 md:p-7 md:pt-12">
                  <h3 className="text-title text-foreground">{a.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-body">{a.text}</p>
                  <ul className="mt-5 mb-7 flex flex-wrap gap-2">
                    {a.points.map((p) => (
                      <li
                        key={p}
                        className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-[13px] text-body-strong"
                      >
                        <CheckCircle2 className="size-3.5 shrink-0 text-success" aria-hidden />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Button asChild variant={i === 0 ? "default" : "outline"} className="mt-auto self-start">
                    <a href={a.ctaTo}>
                      {a.ctaLabel}
                      <ArrowRight />
                    </a>
                  </Button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </Section>

      {/* ─── SUCCESS STORIES ─────────────────────── */}
      <Section id="results">
        <div className="mb-10 max-w-2xl md:mb-12">
          <SectionKicker>{content.results.kicker}</SectionKicker>
          <SectionTitle>
            <EmphasisTitle text={content.results.title} emphasis={content.results.titleEmphasis} />
          </SectionTitle>
          <SectionLead>{content.results.lead}</SectionLead>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {content.results.stories.map((story, i) => (
            <motion.article
              key={story.title}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative min-h-[420px] overflow-hidden rounded-3xl bg-muted shadow-soft"
            >
              <img
                src={story.image}
                alt={story.title}
                loading="lazy"
                className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
              <div className="absolute inset-x-3 bottom-3 rounded-2xl bg-card/95 p-5 shadow-elevated backdrop-blur md:inset-x-4 md:bottom-4 md:p-6">
                <span className={cn("inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium", TONE_BG[i % 2 ? "peach" : "lavender"])}>
                  {story.tag}
                </span>
                <h3 className="mt-3 text-title text-foreground">{story.title}</h3>
                <p className="mt-1.5 text-sm leading-6 text-body">{story.text}</p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-1 rounded-sm text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  View case study
                  <ArrowUpRight className="size-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </Section>

      {/* ─── TESTIMONIALS ────────────────────────── */}
      <Section tinted>
        <SplitHeading
          kicker={content.testimonials.kicker}
          title={content.testimonials.title}
          lead={content.testimonials.lead}
        />
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <motion.figure
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="relative flex min-h-[320px] flex-col justify-between overflow-hidden rounded-3xl bg-primary p-7 text-primary-foreground shadow-panel md:p-10"
          >
            <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-pastel-lavender opacity-30 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-28 -left-16 size-72 rounded-full bg-pastel-peach opacity-25 blur-3xl" />
            <blockquote className="relative text-[22px] leading-[32px] tracking-[-0.4px] md:text-[28px] md:leading-[38px] md:tracking-[-0.6px]">
              <span className="font-serif text-[1.6em] leading-none italic opacity-60" aria-hidden>
                “
              </span>
              {content.testimonials.featured.quote}
            </blockquote>
            <figcaption className="relative mt-10 flex items-center gap-3">
              <Avatar className="size-11 border-2 border-primary-foreground/20">
                <AvatarImage src={content.testimonials.featured.image} alt="" />
                <AvatarFallback>JM</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{content.testimonials.featured.name}</p>
                <p className="text-caption opacity-70">{content.testimonials.featured.role}</p>
              </div>
            </figcaption>
          </motion.figure>
          <div className="flex flex-col gap-4">
            {content.testimonials.items.map((t, i) => (
              <motion.figure
                key={t.name}
                {...fadeUp}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                className="flex flex-1 flex-col justify-between rounded-3xl border border-border bg-card p-6 shadow-soft md:p-7"
              >
                <div className="mb-3 flex gap-0.5 text-pastel-peach-ink" aria-label="5 out of 5">
                  {Array.from({ length: 5 }, (_, s) => (
                    <svg key={s} viewBox="0 0 20 20" className="size-4 fill-current" aria-hidden>
                      <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9z" />
                    </svg>
                  ))}
                </div>
                <blockquote className="text-body-md text-body-strong">
                  {String.fromCharCode(0x201c)}
                  {t.quote}
                  {String.fromCharCode(0x201d)}
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <Avatar className="size-10">
                    <AvatarImage src={t.image} alt="" />
                    <AvatarFallback>{t.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-foreground">{t.name}</p>
                    <p className="text-caption text-muted-foreground">{t.role}</p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </Section>

      {/* ─── FAQ ─────────────────────────────────── */}
      <Section id="faq">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <div>
            <SectionKicker>{content.faq.kicker}</SectionKicker>
            <SectionTitle>{content.faq.title}</SectionTitle>
            <SectionLead>{content.faq.lead}</SectionLead>
            <Button variant="outline" className="mt-8" onClick={() => setContactOpen(true)}>
              <MessageSquare />
              Ask us anything
            </Button>
          </div>
          <Accordion
            type="single"
            collapsible
            value={expandedFaq || ""}
            onValueChange={(v) => setExpandedFaq(v ? v : false)}
            className="space-y-3"
          >
            {content.faq.items.map((faq) => (
              <AccordionItem
                key={faq.q}
                value={faq.q}
                className="rounded-2xl border border-border bg-card px-5 shadow-soft transition-colors data-[state=open]:bg-muted/60 md:px-6"
              >
                <AccordionTrigger className="py-5 text-left text-body-md font-medium text-foreground hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-6 text-body">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* ─── CTA ─────────────────────────────────── */}
      <section className="px-3 pb-6 md:px-6">
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-primary px-6 py-16 text-center text-primary-foreground md:px-12 md:py-24"
        >
          <div aria-hidden className="pointer-events-none absolute -top-40 left-1/2 size-[520px] -translate-x-1/2 rounded-full bg-pastel-lavender opacity-25 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-20 size-[360px] rounded-full bg-pastel-peach opacity-20 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -right-24 -bottom-32 size-[360px] rounded-full bg-pastel-mint opacity-20 blur-3xl" />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(currentColor_1px,transparent_1px),linear-gradient(90deg,currentColor_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"
          />
          <h2 className="relative mx-auto max-w-3xl text-[34px] leading-[40px] font-medium tracking-[-1.2px] md:text-[56px] md:leading-[60px] md:tracking-[-2px]">
            <EmphasisTitle text={content.cta.title} emphasis={content.cta.titleEmphasis} />
          </h2>
          <p className="relative mx-auto mt-5 max-w-xl text-body-md opacity-75">{content.cta.text}</p>
          <div className="relative mt-9 flex flex-col justify-center gap-2.5 sm:flex-row">
            <Button
              size="lg"
              onClick={() => setContactOpen(true)}
              className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
            >
              {content.cta.primaryLabel}
              <ArrowRight />
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-primary-foreground/25 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/auth/register">{content.cta.secondaryLabel}</Link>
            </Button>
          </div>
        </motion.div>
      </section>

      {/* ─── FOOTER ──────────────────────────────── */}
      <footer>
        <div className="mx-auto max-w-6xl px-4 pt-14 pb-8 md:px-6 md:pt-20">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_repeat(4,minmax(0,0.7fr))] lg:gap-8">
            <div>
              <BrandMark />
              <p className="mt-4 max-w-[280px] text-sm leading-6 text-body">{content.footer.tagline}</p>
              <div className="mt-5 flex gap-2">
                {content.footer.socials.map((s) => {
                  const Icon = socialIcons[s.platform as keyof typeof socialIcons] ?? Facebook;
                  return (
                    <Button key={s.platform + s.href} variant="outline" size="icon" asChild aria-label={s.platform}>
                      <a href={s.href}>
                        <Icon />
                      </a>
                    </Button>
                  );
                })}
              </div>
            </div>
            {content.footer.columns.map((col) => (
              <div key={col.title}>
                <h3 className="mb-4 text-sm font-medium text-foreground">{col.title}</h3>
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      {link.href.startsWith("#") ? (
                        <a
                          href={link.href}
                          onClick={(e) => handleScroll(e, link.href)}
                          className="text-sm text-body transition-colors hover:text-foreground"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <a href={link.href} className="text-sm text-body transition-colors hover:text-foreground">
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col gap-3 border-t border-border pt-6 md:flex-row md:items-center md:justify-between">
            <p className="text-caption text-muted-foreground">{content.footer.copyright}</p>
            <div className="flex gap-5">
              {content.footer.legal.map((t) => (
                <a key={t} href="#" className="text-caption text-muted-foreground transition-colors hover:text-foreground">
                  {t}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* ─── CONTACT MODAL (header / CTA triggers) ── */}
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />

      {/* ─── GLOBAL CONTACT FAB (bottom-right, persistent) ── */}
      <ContactFab />
    </div>
  );
}

// ────────────────────────────────────────────────────
// HERO BACKDROP & PRODUCT PREVIEW
// ────────────────────────────────────────────────────
function HeroBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[880px] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-shell via-background to-background" />
      <div className="absolute top-[-180px] left-[8%] size-[520px] rounded-full bg-pastel-lavender opacity-70 blur-[110px]" />
      <div className="absolute top-[-120px] right-[4%] size-[460px] rounded-full bg-pastel-peach opacity-70 blur-[110px]" />
      <div className="absolute top-[260px] left-[38%] size-[420px] rounded-full bg-pastel-mint opacity-50 blur-[120px]" />
      <div className="absolute inset-0 opacity-[0.35] [background-image:linear-gradient(var(--hairline)_1px,transparent_1px),linear-gradient(90deg,var(--hairline)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_70%_55%_at_30%_20%,black,transparent)]" />
    </div>
  );
}

const previewNav = [
  { icon: <LayoutDashboard />, label: "Overview" },
  { icon: <Briefcase />, label: "Placements" },
  { icon: <CalendarDays />, label: "Attendance" },
  { icon: <ClipboardList />, label: "Work logs", active: true },
  { icon: <GraduationCap />, label: "Courses" },
  { icon: <Users />, label: "Students", badge: "2" },
  { icon: <BarChart3 />, label: "Reports" },
];

const previewRows: { day: number; events?: { title: string; time: string; tone: ChipTone; icon: React.ReactNode }[]; note?: string; today?: boolean }[] = [
  {
    day: 1,
    events: [
      { title: "Work log sent", time: "6:45 am", tone: "lavender", icon: <ClipboardList /> },
      { title: "Absent from site", time: "6:00 am", tone: "peach", icon: <Hourglass /> },
    ],
  },
  { day: 2, note: "No events." },
  {
    day: 3,
    events: [
      { title: "Log edited", time: "6:45 am", tone: "rose", icon: <ClipboardCheck /> },
      { title: "Present on site", time: "6:00 am", tone: "mint", icon: <CheckCircle2 /> },
      { title: "Lesson viewed", time: "6:45 am", tone: "sky", icon: <Eye /> },
    ],
  },
  { day: 4, note: "Weekend" },
  {
    day: 5,
    today: true,
    events: [
      { title: "Report downloaded", time: "6:45 am", tone: "lilac", icon: <Download /> },
      { title: "Supervisor review", time: "9:30 am", tone: "sky", icon: <ShieldCheck /> },
    ],
  },
];

const previewDots: Record<number, ChipTone[]> = {
  1: ["mint", "peach"],
  3: ["rose", "mint"],
  5: ["lilac"],
  8: ["mint"],
  9: ["lavender"],
  12: ["peach", "mint"],
  14: ["rose"],
  20: ["mint"],
};

const DOT_INK: Record<ChipTone, string> = {
  lavender: "bg-pastel-lavender-ink",
  peach: "bg-pastel-peach-ink",
  rose: "bg-pastel-rose-ink",
  sky: "bg-pastel-sky-ink",
  mint: "bg-pastel-mint-ink",
  lilac: "bg-pastel-lilac-ink",
  muted: "bg-mute",
};

/** A static, decorative rendering of the product in its new visual language. */
function ProductPreview() {
  return (
    <div
      aria-hidden
      className="pointer-events-none overflow-hidden rounded-[28px] border border-border bg-shell p-2 shadow-[0_40px_120px_-40px_rgb(17_17_19/0.35),0_0_0_1px_rgb(17_17_19/0.03)] select-none md:p-2.5"
    >
      <div className="flex min-h-[420px] md:min-h-[540px]">
        {/* sidebar */}
        <div className="hidden w-[200px] shrink-0 flex-col px-3 pt-3 pb-4 lg:flex">
          <div className="flex items-center justify-between px-2 pb-6">
            <BrandMark size="sm" />
          </div>
          <p className="mb-2 px-2 text-[11px] text-mute">Main menu</p>
          <ul className="space-y-0.5">
            {previewNav.map((n) => (
              <li
                key={n.label}
                className={cn(
                  "flex h-9 items-center gap-2.5 rounded-full px-3 text-[13px] [&_svg]:size-4",
                  n.active ? "bg-card font-medium text-foreground shadow-soft" : "text-body",
                )}
              >
                {n.icon}
                {n.label}
                {n.badge && (
                  <span className="ml-auto flex size-4.5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                    {n.badge}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-auto flex items-center gap-2.5 px-2">
            <span className="flex size-8 items-center justify-center rounded-full bg-pastel-lavender text-[11px] font-medium text-pastel-lavender-ink">
              AJ
            </span>
            <span>
              <span className="block text-[12px] font-medium text-foreground">Amina Juma</span>
              <span className="block text-[11px] text-muted-foreground">Student</span>
            </span>
          </div>
        </div>

        {/* main panel */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[22px] border border-border bg-background">
          <div className="mx-3 mt-3 flex h-12 items-center gap-2 rounded-2xl border border-border px-4 text-[12px] text-muted-foreground">
            <span className="hidden sm:inline">Taskpart</span>
            <span className="hidden opacity-50 sm:inline">›</span>
            <span>Students</span>
            <span className="opacity-50">›</span>
            <span className="font-medium text-foreground">Activity</span>
            <span className="ml-auto flex gap-1.5">
              {[<Bell key="b" />, <MessageSquare key="m" />, <Search key="s" />].map((icon, i) => (
                <span key={i} className="flex size-7 items-center justify-center rounded-full border border-border text-body [&_svg]:size-3.5">
                  {icon}
                </span>
              ))}
            </span>
          </div>

          <div className="flex min-h-0 flex-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <span className="text-[13px] font-medium text-foreground">Activity calendar</span>
                <span className="rounded-full border border-border px-2.5 py-1 text-[11px] text-body">Filter</span>
              </div>
              <div className="flex gap-1.5 overflow-hidden border-b border-border px-4 py-2.5">
                {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"].map((m) => (
                  <span
                    key={m}
                    className={cn(
                      "shrink-0 rounded-full px-4 py-1.5 text-[11px]",
                      m === "Feb" ? "bg-primary font-medium text-primary-foreground" : "bg-muted text-body",
                    )}
                  >
                    {m}
                  </span>
                ))}
              </div>
              <ul>
                {previewRows.map((r) => (
                  <li key={r.day} className="flex min-h-[62px] items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0 md:min-h-[72px]">
                    <span
                      className={cn(
                        "flex size-7 shrink-0 items-center justify-center rounded-full text-[12px]",
                        r.today ? "bg-primary text-primary-foreground" : "text-body",
                      )}
                    >
                      {r.day}
                    </span>
                    {r.events ? (
                      <div className="flex min-w-0 flex-1 gap-2 overflow-hidden">
                        {r.events.map((e) => (
                          <EventChip key={e.title} tone={e.tone} icon={e.icon} title={e.title} meta={e.time} className="shrink-0" />
                        ))}
                      </div>
                    ) : (
                      <span className="text-[12px] text-muted-foreground">{r.note}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="hidden w-[260px] shrink-0 flex-col border-l border-border p-4 md:flex">
              <p className="text-center text-[13px] font-medium text-foreground">February</p>
              <div className="mt-3 grid grid-cols-7 gap-y-1 text-center text-[10px]">
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                  <span key={d} className="pb-1 text-muted-foreground">
                    {d}
                  </span>
                ))}
                {Array.from({ length: 4 }, (_, i) => (
                  <span key={`p${i}`} />
                ))}
                {Array.from({ length: 28 }, (_, i) => {
                  const day = i + 1;
                  const weekend = (i + 4) % 7 >= 5;
                  return (
                    <span
                      key={day}
                      className={cn(
                        "relative mx-auto flex size-7 items-center justify-center rounded-full",
                        day === 5 ? "bg-primary text-primary-foreground" : weekend ? "text-muted-foreground" : "bg-muted text-foreground",
                      )}
                    >
                      {day}
                      {previewDots[day] && day !== 5 && (
                        <span className="absolute bottom-0.5 flex gap-px">
                          {previewDots[day].map((t) => (
                            <span key={t} className={cn("size-1 rounded-full", DOT_INK[t])} />
                          ))}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
              <p className="mt-5 mb-2.5 text-[12px] font-medium text-foreground">Upcoming</p>
              <div className="space-y-2">
                {[
                  { t: "Supervisor visit", m: "10 Feb · 9:00am", tone: "lilac" as ChipTone, b: "2 days left" },
                  { t: "Mid-term report", m: "12 Feb · 5:00pm", tone: "peach" as ChipTone, b: "4 days left" },
                  { t: "Placement review", m: "14 Feb · 10:30am", tone: "sky" as ChipTone, b: "6 days left" },
                ].map((u) => (
                  <div key={u.t} className={cn("rounded-xl p-2.5", TONE_BG[u.tone].split(" ")[0])}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[11px] font-medium text-foreground">{u.t}</p>
                        <p className="truncate text-[10px] text-body">{u.m}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-card px-1.5 py-0.5 text-[9px] font-medium text-foreground">
                        {u.b}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────
// HOW IT WORKS TIMELINE
// ────────────────────────────────────────────────────
function HowItWorksTimeline({ steps }: { steps: LandingContent["howItWorks"]["steps"] }) {
  const tones: ChipTone[] = ["lavender", "peach", "mint", "sky", "lilac"];
  return (
    <ol className="relative grid gap-4 md:grid-cols-5 md:gap-3">
      <div
        aria-hidden
        className="absolute top-7 right-[10%] left-[10%] hidden h-px bg-[linear-gradient(90deg,transparent,var(--hairline-strong)_10%,var(--hairline-strong)_90%,transparent)] md:block"
      />
      {steps.map((step, i) => (
        <motion.li
          key={step.number}
          {...fadeUp}
          transition={{ duration: 0.45, delay: i * 0.08 }}
          className="relative flex gap-4 md:flex-col md:items-center md:text-center"
        >
          <span
            className={cn(
              "relative z-10 flex size-14 shrink-0 items-center justify-center rounded-full border-4 border-background text-[15px] font-medium tabular-nums shadow-soft",
              TONE_BG[tones[i % tones.length]],
            )}
          >
            {step.number}
          </span>
          <div className="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-soft md:mt-5 md:w-full">
            <h3 className="text-body-md font-medium text-foreground">{step.title}</h3>
            <p className="mt-2 text-sm leading-6 text-body">{step.text}</p>
          </div>
        </motion.li>
      ))}
    </ol>
  );
}

// ────────────────────────────────────────────────────
// SHARED COMPONENTS
// ────────────────────────────────────────────────────
function Section({ id, children, tinted }: { id?: string; children: React.ReactNode; tinted?: boolean }) {
  return (
    <section id={id} className={cn("scroll-mt-24", tinted && "px-3 md:px-6")}>
      <div
        className={cn(
          "mx-auto py-16 md:py-24",
          tinted ? "max-w-[1240px] rounded-[32px] bg-shell px-4 md:px-10 lg:px-[calc((1240px-72rem)/2+1.5rem)]" : "max-w-6xl px-4 md:px-6",
        )}
      >
        {children}
      </div>
    </section>
  );
}

function SectionKicker({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-[13px] font-medium text-body shadow-soft">
      <span className="size-1.5 rounded-full bg-foreground" aria-hidden />
      {children}
    </p>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[32px] leading-[38px] font-medium tracking-[-1.1px] text-balance text-foreground md:text-[48px] md:leading-[54px] md:tracking-[-1.8px]">
      {children}
    </h2>
  );
}

function SectionLead({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={cn("mt-5 max-w-lg text-body-md text-body", className)}>{children}</p>;
}

function SplitHeading({
  kicker,
  title,
  lead,
}: {
  kicker: string;
  title: React.ReactNode;
  lead: string;
}) {
  return (
    <div className="mb-10 grid gap-5 md:mb-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-end lg:gap-16">
      <div>
        <SectionKicker>{kicker}</SectionKicker>
        <SectionTitle>{title}</SectionTitle>
      </div>
      <p className="max-w-lg text-body-md text-body">{lead}</p>
    </div>
  );
}

type Stat = LandingContent["stats"][number];

const statTones: ChipTone[] = ["lavender", "peach", "mint", "sky"];

function StatTile({ stat, index }: { stat: Stat; index: number }) {
  const dark = stat.kind === "chart-bar-dark";
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.45, delay: index * 0.07 }}
      className={cn(
        "flex min-h-[176px] flex-col justify-between rounded-3xl p-5 shadow-soft md:min-h-[200px] md:p-6",
        dark ? "bg-primary text-primary-foreground" : cn("border border-border", TONE_BG[statTones[index % statTones.length]].split(" ")[0]),
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className={cn("text-sm font-medium", dark ? "opacity-75" : "text-body-strong")}>{stat.label}</p>
        <span className={cn("flex size-8 items-center justify-center rounded-full", dark ? "bg-primary-foreground/10" : "bg-card shadow-[0_1px_2px_rgb(0_0_0/0.06)]")}>
          <ArrowUpRight className="size-4" />
        </span>
      </div>
      <div className="mt-6 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className={cn("text-[30px] leading-9 font-medium tracking-[-1px] tabular-nums md:text-[34px] md:leading-10", !dark && "text-foreground")}>
            {stat.value}
          </p>
          <p className={cn("mt-1 flex items-center gap-1 text-caption", dark ? "opacity-70" : "text-body")}>
            {stat.kind === "status" ? (
              <span className="size-1.5 rounded-full bg-success" aria-hidden />
            ) : (
              <TrendingUp className="size-3" aria-hidden />
            )}
            {stat.delta}
          </p>
        </div>
        <MiniChart kind={stat.kind} />
      </div>
    </motion.div>
  );
}

function MiniChart({ kind }: { kind: string }) {
  if (kind === "chart-line") {
    return (
      <svg width="76" height="40" viewBox="0 0 80 40" fill="none" className="shrink-0 text-foreground" aria-hidden>
        <path d="M2 32 L14 24 L26 28 L40 14 L54 18 L66 8 L78 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="78" cy="12" r="3" className="fill-current" />
      </svg>
    );
  }
  if (kind === "chart-bar-dark") {
    const heights = [14, 22, 18, 30, 22, 32, 26, 38];
    return (
      <svg width="76" height="40" viewBox="0 0 90 40" fill="none" className="shrink-0" aria-hidden>
        {heights.map((h, i) => (
          <rect
            key={i}
            x={i * 11}
            y={40 - h}
            width="7"
            height={h}
            rx="3.5"
            className={i === heights.length - 1 ? "fill-pastel-lavender" : "fill-primary-foreground/25"}
          />
        ))}
      </svg>
    );
  }
  if (kind === "chart-donut") {
    return (
      <svg width="44" height="44" viewBox="0 0 50 50" className="shrink-0" aria-hidden>
        <circle cx="25" cy="25" r="18" strokeWidth="6" fill="none" className="stroke-card" />
        <circle cx="25" cy="25" r="18" strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray="80 113" transform="rotate(-90 25 25)" className="stroke-foreground" />
      </svg>
    );
  }
  return null;
}
